/*
 *  Collection Template
 *    Contains all the scripts used for the collection template.
 *
 *  Version:
 *    1.0.0 - 2020/07/30
 */

import $ from 'jquery';
import * as noUiSlider from 'nouislider';
import { refreshThumbnails } from './../objects/product/ProductThumbnail';

// Ajax Collection Imports
import {
  AjaxCollectionTemplate, SelectSorter,

  TagFilter, PriceRangeFilter,

  VO_MODE_PRODUCT, OPERATION_OR, LoadMore, SmartTagCheckboxFacet
} from './../process/ajax-collection-v3';

// Custom Filters & Facets
import { CustomAvailabilityFilter } from './../components/filters/CustomAvailabilityFilter';
import { SmartAvailabilityCheckboxFacet } from './../components/facets/SmartAvailabilityCheckboxFacet';
import { CustomPriceSlideFacet } from './../components/facets/CustomPriceSlideFacet';

import { ProductThumbnailPrint } from './../objects/product/ProductThumbnailPrint';

import { InfiniteScroll } from './../components/pagination/InfiniteScroll';

// Selectors
export const CONTAINER_SELECTOR = '[data-collection-template]';
export const PRODUCT_LOOP_CONTAINER = '[data-collection-template-products-outer]';
export const SORT_SELECT_SELECTOR = '[data-collection-template-sort]';
export const TOGGLE_FILTERS = '[data-collection-template-toggle-filters]';
export const NO_RESULTS_SELECTOR = '[data-collection-no-results]';
export const COLLECTION_DISPLAY_MODE = 'data-collection-dm';
export const READ_MORE_CONTAINER = '[data-read-more-container]';
export const READ_MORE_TOGGLE = '[data-read-more-toggle]';
export const BACK_TO_TOP_SELECTOR = '[data-collection-back-to-top]';
export const NUM_FILTERS = '[data-collection-template-num-filters]';

// Faceted Nav Selectors
export const FACETED_NAV_SELECTOR = '[data-faceted-nav]';
export const FACETED_NAV_GROUP = '[data-faceted-nav-group]';
export const FACETED_NAV_TITLE = '.c-collection-faceted-nav__title';
export const FACETED_NAV_AVAILABILITY_SELECTOR = '[data-faceted-nav-availability]';
export const FACETED_NAV_STYLE_SELECTOR = '[data-faceted-nav-style]';
export const FACETED_NAV_PRODUCTS_SELECTOR = '[data-faceted-nav-products]';
export const FACETED_NAV_ROOM_SELECTOR = '[data-faceted-nav-room]';
export const FACETED_NAV_BRAND_SELECTOR = '[data-faceted-nav-brand]';
export const FACETED_NAV_COLOUR_SELECTOR = '[data-faceted-nav-colour]';
export const FACETED_NAV_MATERIAL_SELECTOR = '[data-faceted-nav-material]';
export const FACETED_NAV_COLLECTIONS_SELECTOR = '[data-faceted-nav-collections]';
export const FACETED_NAV_PRICE_SELECTOR = '[data-faceted-nav-price]';
export const FACETED_NAV_CLEAR_FILTERS = '[data-collection-template-clear-filters]';
export const FACETED_NAV_CLEAR_FILTERS_ROW = '[data-collection-template-clear-row]';
export const FACETED_NAV_ACTIVE_FILTERS_CONTAINER = '[data-collection-template-active-filters-container]';
export const FACETED_NAV_ACTIVE_FILTER = 'data-collection-active-filter';
export const FACETED_NAV_ACTIVE_FILTER_INDEX = 'data-collection-active-filter-index';
export const FACETED_NAV_ACTIVE_FILTER_TYPE = 'data-collection-active-filter-type';
export const FACETED_NAV_CHECKBOX = '[data-facet-checkbox]';
export const FACETED_NAV_COUNT = '[data-collection-template-facet-count]';

// Classes
export const CLASS_FILTERS_OPEN = 'is-filters-open';
export const CLASS_EXPANDED = 'is-expanded';
export const CLASS_PRODUCT_THUMB = 'c-collection-template__product';
export const CLASS_VISIBLE = 'is-visible';
export const CLASS_OPEN = 'is-open';
export const CLASS_MIN_HEIGHT = 'has-min-height';
export const CLASS_PRODUCTS_CONTAINER = 'c-collection-template__products';

// Attributes
export const ATTR_CONTENT_BLOCK_POSITION = 'data-content-block-position';

class CollectionTemplate extends AjaxCollectionTemplate {
  constructor(container) {
    super(container);

    // Selectors
    this.productLoopContainer = container.find(PRODUCT_LOOP_CONTAINER);

    // Setup Display Mode
    if(this.container.attr(COLLECTION_DISPLAY_MODE) == 'variants') {
      this.draw.setDisplayMode('AllAvailableVariants');
      this.isVariant = true;
    } else {
      this.draw.setDisplayMode('FirstAvailableVariant');
    }

    // Setup Pagination
    this.pagination.setPaginator(new InfiniteScroll(this, container));

    // Setup Sort
    this.sort.addSorter(new SelectSorter(this, $(SORT_SELECT_SELECTOR)));

    // Setup Filters
    [
      this.availableFilter = new CustomAvailabilityFilter(this, 'in-stock'),
      this.styleFilter =  new TagFilter(this, 'style', OPERATION_OR),
      this.productsFilter =  new TagFilter(this, 'products', OPERATION_OR),
      this.roomFilter =  new TagFilter(this, 'room', OPERATION_OR),
      this.brandFilter =  new TagFilter(this, 'brand', OPERATION_OR),
      this.colourFilter =  new TagFilter(this, 'colour', OPERATION_OR),
      this.materialFilter =  new TagFilter(this, 'material', OPERATION_OR),
      this.collectionsFilter =  new TagFilter(this, 'collections', OPERATION_OR),
      this.priceFilter =  new PriceRangeFilter(this, 'price')
    ].forEach(e => this.filters.addFilter(e));

    // Setup Facets
    [
      this.availableFacet = new SmartAvailabilityCheckboxFacet({
        template: this, filter: this.availableFilter,
        title: `${Language.strings['collections.filters.available_title']}`,
        container: FACETED_NAV_AVAILABILITY_SELECTOR, prefixes: ['In Stock']
      }),
      this.styleFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.styleFilter,
        title: `${Language.strings['collections.filters.style_title']}`,
        container: FACETED_NAV_STYLE_SELECTOR, prefixes: ['Style']
      }),
      this.productsFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.productsFilter,
        title: `${Language.strings['collections.filters.products_title']}`,
        container: FACETED_NAV_PRODUCTS_SELECTOR, prefixes: ['Products']
      }),
      this.roomFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.roomFilter,
        title: `${Language.strings['collections.filters.room_title']}`,
        container: FACETED_NAV_ROOM_SELECTOR, prefixes: ['Room']
      }),
      this.brandFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.brandFilter,
        title: `${Language.strings['collections.filters.brand_title']}`,
        container: FACETED_NAV_BRAND_SELECTOR, prefixes: ['Brand']
      }),
      this.colourFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.colourFilter,
        title: `${Language.strings['collections.filters.colour_title']}`,
        container: FACETED_NAV_COLOUR_SELECTOR, prefixes: ['Colour']
      }),
      this.materialFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.materialFilter,
        title: `${Language.strings['collections.filters.material_title']}`,
        container: FACETED_NAV_MATERIAL_SELECTOR, prefixes: ['Material']
      }),
      this.collectionsFacet = new SmartTagCheckboxFacet({
        template: this, filter: this.collectionsFilter,
        title: `${Language.strings['collections.filters.collections_title']}`,
        container: FACETED_NAV_COLLECTIONS_SELECTOR, prefixes: ['Collections']
      }),
      this.priceFacet = new CustomPriceSlideFacet({
        template: this, filter: this.priceFilter,
        title: `${Language.strings['collections.filters.price_title'] }`,
        container: FACETED_NAV_PRICE_SELECTOR, noUiSlider
      })
    ].forEach(facet => this.filters.addFacet(facet));

    // Elements
    this.facets = container.find(FACETED_NAV_SELECTOR);
    this.count = container.find(FACETED_NAV_COUNT);
    this.noResults = container.find(NO_RESULTS_SELECTOR);
    this.clearFiltersRow = container.find(FACETED_NAV_CLEAR_FILTERS_ROW);
    this.activeFiltersContainer = container.find(FACETED_NAV_ACTIVE_FILTERS_CONTAINER);
    this.facetToggle = [this.facets, $('body')];
    this.readMoreContainer = container.find(READ_MORE_CONTAINER);

    this.checkPosition();

    // Events
    container.on('click touchend', TOGGLE_FILTERS, e => this.onFiltersToggle(e));
    container.on('click touchend', FACETED_NAV_TITLE, e => this.onFacetToggle(e));
    container.on('click touchend', FACETED_NAV_CLEAR_FILTERS, e => this.onClearFilters(e));
    container.on('click touchend', `[${FACETED_NAV_ACTIVE_FILTER}]`, e => this.onActiveFilterClear(e));
    container.on('click touchend', READ_MORE_TOGGLE, e => this.onReadMoreToggle(e));
    $(document).on('click', BACK_TO_TOP_SELECTOR, e => this.scrollToTop(e))
  }

  checkPosition() {
    let top = $(window).scrollTop();
    let pageHeight = $('html').height();
    let scrollpoint = pageHeight - (pageHeight/2);

    if(top > scrollpoint) {
      $(BACK_TO_TOP_SELECTOR).addClass(CLASS_VISIBLE);
    } else {
      $(BACK_TO_TOP_SELECTOR).removeClass(CLASS_VISIBLE);
    }

    requestAnimationFrame(() => this.checkPosition());
  }

  generatePrint(v, index) {
    let p = v.product;
    let url = this.getVariantUrl(v);
    let p2 = { ...p };//Duplicate
    p2.variants = p2.variants.map(variant => {
      let v2 = { ...variant };//Duplicate
      delete v2.product;//Remove circular reference
      return v2;
    });

    //Check if product has a discounted price.
    let discounted = false;
    let saving = 0;
    let compare = v.compare_at_price;
    if(compare > v.price) {
      discounted = true;
      saving = compare - v.price;
    }

    let available = this.isVariantAvailable(v);

    return ProductThumbnailPrint(p, p2, url, discounted, saving, available, compare, v);
  }

  getContentBlockPosition(cb, thumbnails) {
    if(thumbnails.length <= 0) return -1; //No thumbs? hide
    let pos = parseInt(cb.attr(ATTR_CONTENT_BLOCK_POSITION));
    if(isNaN(pos) || !isFinite(pos)) return 9999;
    if(pos > thumbnails.length + 1) {
      cb.removeClass(CLASS_VISIBLE);
    } else {
      cb.addClass(CLASS_VISIBLE);
    }
    return pos;
  }

  onActiveFilterClear(e) {
    e.preventDefault();
    e.stopPropagation();

    let self = $(e.currentTarget);
    let filter = self.attr(FACETED_NAV_ACTIVE_FILTER);
    let index = parseInt(self.attr(FACETED_NAV_ACTIVE_FILTER_INDEX));
    let type = self.attr(FACETED_NAV_ACTIVE_FILTER_TYPE);

    if(!filter || index < 0 || !type) return;

    switch(type) {
      case "tags":
        this.filters.filters[index].removeTag(filter);
        break;
      case "range":
        this.filters.filters[index].clear();
      case "availability":
        this.filters.filters[index].setAvailability(false);
      break;
    }
  }

  onClearFilters(e) {
    e.stopPropagation();
    e.preventDefault();
    this.filters.clearFilters();
  }

  onFilter() {
    this.clearFiltersRow.addClass(CLASS_VISIBLE);

    //Keys must be in the order of the filters defined above. Any changes
    //to the filters must be mimmicked here.
    let keys = [
      'available', 'tags', 'tags', 'tags', 'tags', 'tags', 'tags', 'tags', 'range'
    ];

    //Loop through filters and find active values
    let print = this.filters.filters.reduce((x, filter, i) => {
      let key = keys[i];
      let activeFilters = filter[key];
      if(!activeFilters || !activeFilters.length) return x; //No active filters for this filter

      return x += activeFilters.reduce((y,af) => {
        return y += `
          <span
            class="c-collection-faceted-nav__clear-filter"
            data-collection-active-filter-index="${i}"
            data-collection-active-filter="${af}"
            data-collection-active-filter-type="${key}"
          >
            ${af.split('_').length > 1 ? `${af.split('_')[1]}` : `${af}`}
            <svg class="o-icon c-collection-faceted-nav__clear-filter-icon" viewBox="0 0 9 9" xmlns="http://www.w3.org/2000/svg"><g fill="#404041" fill-rule="nonzero"><path d="M6.96 7.93l-6.5-6.5A.82.82 0 01.43.27a.82.82 0 011.16 0l6.5 6.5c.31.32.32.84 0 1.16a.8.8 0 01-1.14 0z"/><path d="M8.1 1.42l-6.5 6.5a.82.82 0 01-1.16 0 .82.82 0 01.01-1.14l6.5-6.5a.82.82 0 011.15 0 .8.8 0 010 1.14z"/></g></svg>
          </span>
        `;
      }, '');
    }, '');

    this.activeFiltersContainer.html(print);
  }

  onFacetToggle(e) {
    e.preventDefault();
    e.stopPropagation();

    let self = $(e.currentTarget);
    let group = self.parent(FACETED_NAV_GROUP);

    group.toggleClass(CLASS_EXPANDED);
  }

  onFiltersToggle(e) {
    e.preventDefault();
    e.stopPropagation();

    this.facetToggle.forEach(e => e.toggleClass(CLASS_FILTERS_OPEN));
  }

  onVariantsDrawn(variants) {
    if(this.filters.isFiltering()) {
      this.onFilter();
    }  else {
      this.clearFiltersRow.removeClass(CLASS_VISIBLE);
    }

    let checkedFilters = 0;
    $(FACETED_NAV_CHECKBOX).each((i, e) => {
      if($(e).prop("checked") == true) checkedFilters ++;
    });
    $(NUM_FILTERS).get(0).innerHTML = `(${checkedFilters})`;

    if(variants.length) {
      this.noResults.removeClass(CLASS_VISIBLE);
      refreshThumbnails();

      // Have we finished fetching all of our data?
      if(this.fetch.isDataFetched()) this.productLoopContainer.removeClass(CLASS_MIN_HEIGHT);

      // Setup wishlist event listeners
      console.log('onVariantsDrawn');
      if(window._swat){
        window._swat.initializeActionButtons('.c-collection-template__products');
      }else{
        window.SwymCallbacks = window.SwymCallbacks || [];

        window.SwymCallbacks.push(function(){
          window._swat.initializeActionButtons('.c-collection-template__products');
        });
      }
      return;
    }

    // Show empty message
    this.noResults.addClass(CLASS_VISIBLE);
  }

  onReadMoreToggle(e) {
    e.preventDefault(), e.stopPropagation();

    // Toggle read more class
    this.readMoreContainer.toggleClass(CLASS_OPEN);
  }

  scrollToTop(e) {
    e.preventDefault(), e.stopPropagation();

    $('html, body').animate({
      scrollTop: 0
    }, 700);
  }
}

/*** Initialize Collection Template ***/
$(document).ready(() => {
  // Find container
  let container = $(CONTAINER_SELECTOR);
  if(!container.length) return;
  if(container.attr('data-initialized')) return;

  // Create the template, we use window.collection for TESTING only!!
  let template = window.collection = new CollectionTemplate(container);

  // Now initialize it
  template.init();
});